import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import cx from 'classnames';
import './SwapPanel.css';

// Keep in step with SwapPanel.css.
const EXIT_MS = 150;
const ENTER_MS = 150;
const SETTLE_SLACK_MS = 60;

const PHASE = { idle: 'idle', exit: 'exit', enter: 'enter' };

/**
 * QHS SwapPanel
 * -------------
 * Renders `children` for the current `activeKey`. When the key changes
 * the content on screen fades out first, then the new content is
 * mounted, the box height glides from the old height to the new, and
 * the new content fades in from below. Two short phases, nothing
 * abrupt; fades only under prefers-reduced-motion.
 *
 * The outgoing content keeps rendering for the 120ms of its fade - the
 * element the parent last rendered for that key - and the new content
 * is mounted only after it is gone, so a form inside never exists
 * twice and field ids never collide.
 *
 *   <SwapPanel activeKey={mode}>{mode === 'a' ? <A /> : <B />}</SwapPanel>
 */
const SwapPanel = ({ activeKey, className, children }) => {
  // The element last rendered for each key, so the outgoing one can
  // stay on screen while it fades although the parent has moved on.
  const rendered = useRef({});
  rendered.current[activeKey] = children;

  const [shownKey, setShownKey] = useState(activeKey);
  const [phase, setPhase] = useState(PHASE.idle);
  const frameRef = useRef(null);
  const paneRef = useRef(null);
  const fromHeightRef = useRef(null);

  // A new key: fade the current content out, then show the new one.
  useEffect(() => {
    if (activeKey === shownKey) return undefined;
    fromHeightRef.current = frameRef.current ? frameRef.current.offsetHeight : null;
    setPhase(PHASE.exit);
    const timer = setTimeout(() => {
      setShownKey(activeKey);
      setPhase(PHASE.enter);
    }, EXIT_MS);
    return () => clearTimeout(timer);
  }, [activeKey, shownKey]);

  // The new content is in the DOM: glide the height and fade it in.
  useLayoutEffect(() => {
    if (phase !== PHASE.enter) return undefined;
    const frame = frameRef.current;
    const pane = paneRef.current;
    if (!frame || !pane) {
      setPhase(PHASE.idle);
      return undefined;
    }
    const from = fromHeightRef.current;
    const to = pane.offsetHeight;
    pane.classList.add('qhs-swap__pane--enter');
    if (from !== null && from !== to) {
      frame.classList.add('qhs-swap--animating');
      frame.style.height = `${from}px`;
    }
    // Flush the start state before setting the end state.
    void frame.offsetHeight; // eslint-disable-line no-void
    if (from !== null && from !== to) frame.style.height = `${to}px`;
    pane.classList.add('qhs-swap__pane--in');

    const timer = setTimeout(() => {
      frame.classList.remove('qhs-swap--animating');
      frame.style.height = '';
      pane.classList.remove('qhs-swap__pane--enter', 'qhs-swap__pane--in');
      setPhase(PHASE.idle);
    }, ENTER_MS + SETTLE_SLACK_MS);
    return () => clearTimeout(timer);
  }, [phase, shownKey]);

  return (
    <div ref={frameRef} className={cx('qhs-swap', className)}>
      <div
        key={String(shownKey)}
        ref={paneRef}
        className={cx('qhs-swap__pane', phase === PHASE.exit && 'qhs-swap__pane--exit')}
      >
        {rendered.current[shownKey]}
      </div>
    </div>
  );
};

export { SwapPanel };
export default SwapPanel;
